import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  Order,
  Stop,
  orderViewsT,
  OrderView,
  createOrderWithDetailsAndFilesT,
  CreateOrderReq,
} from "./types";
import * as notifications from "../../app/notifications";
import * as tPromise from "io-ts-promise";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { convertStringToDecimal } from "../../../helpers/parse-decimal";
import axios, { isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { reportWarning } from "../../../helpers/report-log-entries";
import { apiFetch } from "../../../helpers/api-fetch";

const prefix = "data/orders";

// The request never got a response: the connection dropped, the browser
// blocked it, or axios's own timeout fired. WEBBOOKING-6 / DORA-323: a direct
// S3 attachment PUT came back with status 0 and never reached our backend.
// Not a bug on our side, so it's logged as a warning rather than a Sentry error.
const isNetworkError = (e: unknown) =>
  isAxiosError(e) &&
  (e.message === "Network Error" ||
    e.message === "Request aborted" ||
    e.message === "timeout exceeded");

type State = {
  myOrders: OrderView[];
  orderInView: OrderView | null;
};

const initialState: State = {
  myOrders: [],
  orderInView: null,
};

const mapStop = (stop: Stop) => ({
  ...stop,
  time: stop.time ? stop.time.split("-").join(" - ") : null,
  lm: convertStringToDecimal(stop.lm).toString(),
  weight: stop.weight || null,
  colli: stop.colli || null,
  width: stop.width || null,
  height: stop.height || null,
  length: stop.length || null,
  cubicMeters: stop.cubicMeters || null,
  // TODO: consider this
  // weight: stop.weight ? convertStringToDecimal(stop.weight).toString() : null,
  // colli: stop.colli ? convertStringToDecimal(stop.colli).toString() : null,
  // width: stop.width ? convertStringToDecimal(stop.width).toString() : null,
  // height: stop.height ? convertStringToDecimal(stop.height).toString() : null,
  // length: stop.length ? convertStringToDecimal(stop.length).toString() : null,
  // cubicMeters: stop.cubicMeters
  //   ? convertStringToDecimal(stop.cubicMeters).toString()
  //   : null,
});

export const loadMyOrders = createErrorReportingAsyncThunk(
  `${prefix}/load-my-orders`,
  async () => {
    const result = await apiFetch("/web-booking-api/orders");
    if (!result.ok) {
      throw new Error("Error loading data");
    }
    return result.json().then(tPromise.decode(orderViewsT));
  }
);

export const createOrder = createAsyncThunk(
  `${prefix}/create-order`,
  async (
    orderPayload: {
      order: Omit<CreateOrderReq, "filesReferences">;
      files: Array<File>;
    },
    { dispatch }
  ) => {
    try {
      const { order, files } = orderPayload;
      // const formData = new FormData();
      const filesReferences: {
        s3Identifier: string;
        name: string;
        mimeType: string;
      }[] = [];
      if (files.length) {
        for (let f of files) {
          // TODO: in parallel
          const res = await apiFetch(
            "/web-booking-api/orders/document-upload-url",
            { method: "POST" }
          );
          const data = (await res.json()) as {
            uploadUrl: string;
            s3Identifier: string;
          };
          const buffer = await f.arrayBuffer();
          await axios.put(data.uploadUrl, buffer, {
            headers: {
              "Content-Type": f.type,
            },
          });

          // Collect file reference data
          filesReferences.push({
            s3Identifier: data.s3Identifier,
            name: f.name,
            mimeType: f.type,
          });
        }
      }
      const data = {
        ...order,
        pickups: order.pickups.map(mapStop),
        dropoffs: order.dropoffs.map(mapStop),
      };
      await axios.post(
        "/web-booking-api/orders/new",
        createOrderWithDetailsAndFilesT.encode({
          ...data,
          filesReferences,
        })
      );
      dispatch(notifications.notify({ message: "Ordre oprettet" }));
    } catch (err) {
      dispatch(
        notifications.notify({
          message: "Der opstod en fejl under ordreoprettelsen",
          level: "error",
        })
      );
      // Callers catch the rethrow to keep the user on the form, so report
      // here or the failure would never reach Sentry.
      if (isNetworkError(err)) {
        reportWarning({ type: "Create order failed", err });
      } else {
        Sentry.captureException(err);
      }
      throw err;
    }
  }
);

const slice = createSlice({
  initialState,
  name: prefix,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadMyOrders.fulfilled, (state, action) => {
      state.myOrders = action.payload;
    });
  },
});

export default slice.reducer;
